import { z } from 'zod';
import { type ParseResult, type SchemaIssue, voyageMasterSchema } from './schemas.js';

/**
 * Ofisin sefer talebi (ADR 0083 §5; sahip kararı 2026-10-19, soru 1: seferi hem gemi hem ofis
 * açabilir). Gemi verisini yalnız gemi sunucusu yazar (mimari belge §8): ofis talep gönderir, gemi
 * uygular ya da gerekçeyle reddeder (ADR 0063 §7 kalıbı). Talep değişmez; içerik hash'i sonuçta da
 * taşınır.
 */
export const VOYAGE_REQUEST_ACTIONS = ['create_voyage', 'link', 'unlink'] as const;
export type VoyageRequestAction = (typeof VOYAGE_REQUEST_ACTIONS)[number];

export const VOYAGE_REQUEST_OUTCOMES = ['applied', 'rejected'] as const;
export type VoyageRequestOutcome = (typeof VOYAGE_REQUEST_OUTCOMES)[number];

/** Not ve ret gerekçesi en çok 1000 karakter; bir talepte en çok 200 rapor. */
export const VOYAGE_REQUEST_TEXT_MAX = 1000;
export const VOYAGE_REQUEST_REPORTS_MAX = 200;

const uuid = z.uuid('INVALID');

export const voyageRequestSchema = z
  .object({
    action: z.enum(VOYAGE_REQUEST_ACTIONS, 'INVALID_OPTION'),
    voyageId: uuid.nullable(),
    reportIds: z.array(uuid).max(VOYAGE_REQUEST_REPORTS_MAX, 'TOO_MANY'),
    voyage: voyageMasterSchema.nullable(),
  })
  .superRefine((value, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (new Set(value.reportIds).size !== value.reportIds.length) issue('reportIds', 'DUPLICATE');
    if (value.action === 'create_voyage') {
      if (value.voyage === null) issue('voyage', 'REQUIRED');
      if (value.voyageId !== null) issue('voyageId', 'NOT_ALLOWED');
    } else {
      if (value.voyageId === null) issue('voyageId', 'REQUIRED');
      if (value.voyage !== null) issue('voyage', 'NOT_ALLOWED');
      if (value.reportIds.length === 0) issue('reportIds', 'REQUIRED');
    }
  });
export type VoyageRequestContent = z.output<typeof voyageRequestSchema>;

export function parseVoyageRequest(input: unknown): ParseResult<VoyageRequestContent> {
  const result = voyageRequestSchema.safeParse(input);
  if (result.success) return { ok: true, value: result.data };
  const issues: SchemaIssue[] = result.error.issues.map((i) => ({
    path: i.path.map(String).join('.'),
    code: i.message,
  }));
  return { ok: false, issues };
}

/** Talebin durumu: sonucu yoksa bekliyor. */
export type VoyageRequestStatus = 'pending' | VoyageRequestOutcome;

export function voyageRequestStatus(outcome: string | null | undefined): VoyageRequestStatus {
  return outcome === 'applied' || outcome === 'rejected' ? outcome : 'pending';
}
